import React, { useState, useEffect, useCallback, memo, useRef } from "react";
import Grid from '@mui/material/Grid';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import styles from '../inputFields/styles.js';
import './styles/TagContainer.css';
import TagDropdown from "./TagDropdown";
import TagTextFielsEditable from "./TagTextFielsEditable";
import TagNumberFiels from "./TagNumberFiels";
import TagDecimalField from "./TagDecimalFields.js";
import TagDatetimeField from "./TagDatetimeFields.js";
import TagCheckBox from "./TagCheckBox.js";
import TagDatePicker from "./TagDatePicker.js";
import TagTimePicker from "./TagTimePicker.js";
import TagMemo from "./TagMemoFields.js";
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const StyledToggleButtonGroup = (props) => (
  <ToggleButtonGroup
    {...props}
    sx={{
      '& .MuiToggleButtonGroup-grouped': {
        margin: 0,
        border: 0,
        flexWrap: 'wrap',
      },
      ...props.sx,
    }}
  />
);

const StyledToggleButton = (props) => (
  <ToggleButton
    {...props}
    sx={{
      padding: 0,
      textTransform: 'none',
      display: 'block',
      width: '100%',
      '&:focus .label-wrapper-no-color, &:focus-visible .label-wrapper-no-color': {
        cursor: 'pointer',
        border: '2px solid #337AB7 !important',
        filter: 'brightness(95%)',
        transform: 'scale(1.05)',
        position: 'relative',
      },
      ...props.sx,
    }}
  />
);

const theme = createTheme({
  palette: {
    action: {
      active: "#221d26"
    },
    text: {
      primary: "#221d26"
    }
  }
});


// TagField component... (remains the same)
const TagField = memo(({ item, onChange, onAlert, variant, element, isTagChecked }) => {
  const handleFieldChange = useCallback((fieldData) => {
    onChange({
      name: fieldData.name,
      value: fieldData.value,
      isValid: fieldData.isValid
    });
  }, [onChange]);

  if (item.newline) return <Box width="100%" key={`newline-${item.param}`} />;

  // Determine validation case for this field
  const getValidationCase = () => {
    const alwaysOn = element?.alwaysOn === 1;
    const isOnlyAddWithVariable = element?.isOnlyAddwithVariable;
    const fieldRequired = item.required === "1" || item.required === 1 || item.required === true;

    if (alwaysOn && fieldRequired) {
      return 'case_4'; // Tag is required, Variable Value is required
    } else if (!alwaysOn && (isOnlyAddWithVariable === false || isOnlyAddWithVariable === null) && fieldRequired) {
      return 'case_2'; // Tag is not required, but if tag is selected then the Variable is required
    }
    return 'none';
  };

  const validationCase = getValidationCase();
  // const isFieldRequired = validationCase === 'case_4' || (validationCase === 'case_2' && isTagChecked); // Keep if needed for logic

  switch(item.type) {
    case "integer":
      return (
        <TagNumberFiels
          key={`number-${item.param}`}
          label={item.prompt}
          param={item.param}
          variant="outlined" // Ensure variant is passed
          minNum={item.min}
          maxNum={item.max}
          prompt={item.mo}
          required={item.required}
          onAlert={onAlert}
          onChange={handleFieldChange}
          info={element}
          value={item.value}
          isAlwaysOn={element?.alwaysOn}
          validationCase={validationCase}
          isTagChecked={isTagChecked}
        />
      );
    case "varchar":
      return (
        <TagTextFielsEditable
          key={`text-${item.param}`}
          label={item.prompt}
          param={item.param}
          variant={variant} // Ensure variant is passed
          prompt={item.mo}
          maxLength={item.length}
          required={item.required}
          onAlert={onAlert}
          onChange={handleFieldChange}
          value={item.value}
          isAlwaysOn={element?.alwaysOn}
          validationCase={validationCase}
          isTagChecked={isTagChecked}
        />
      );
    // ... other cases remain the same ...
     case "decimal":
      return (
        <TagDecimalField
          key={`decimal-${item.param}`}
          label={item.prompt}
          param={item.param}
          variant={variant}
          prompt={item.mo}
          minNum={item.min}
          maxNum={item.max}
          required={item.required}
          onAlert={onAlert}
          onChange={handleFieldChange}
          value={item.value}
          isAlwaysOn={element?.alwaysOn}
          validationCase={validationCase}
          isTagChecked={isTagChecked}
        />
      );
    case "datetime":
      return (
        <TagDatetimeField
          key={`datetime-${item.param}`}
          label={item.prompt}
          param={item.param}
          variant={variant}
          prompt={item.mo}
          required={item.required}
          onAlert={onAlert}
          onChange={handleFieldChange}
          value={item.value}
          isAlwaysOn={element?.alwaysOn}
          validationCase={validationCase}
          isTagChecked={isTagChecked}
        />
      );
    case "date":
      return (
        <TagDatePicker
          key={`date-${item.param}`}
          label={item.prompt}
          param={item.param}
          variant={variant}
          prompt={item.mo}
          required={item.required}
          onAlert={onAlert}
          onChange={handleFieldChange}
          value={item.value}
          isAlwaysOn={element?.alwaysOn}
          validationCase={validationCase}
          isTagChecked={isTagChecked}
        />
      );
    case "time":
      return (
        <TagTimePicker
          key={`time-${item.param}`}
          label={item.prompt}
          param={item.param}
          variant={variant}
          prompt={item.mo}
          required={item.required}
          onAlert={onAlert}
          onChange={handleFieldChange}
          value={item.value}
          isAlwaysOn={element?.alwaysOn}
          validationCase={validationCase}
          isTagChecked={isTagChecked}
        />
      );
    case "dropdown":
      return (
        <TagDropdown
          key={`dropdown-${item.param}`}
          label={item.prompt}
          param={item.param}
          variant={variant}
          prompt={item.mo}
          maxLength={item.length}
          required={item.required}
          onAlert={onAlert}
          onChange={handleFieldChange}
          choices={item.choices}
          value={item.value}
          isAlwaysOn={element?.alwaysOn}
          validationCase={validationCase}
          isTagChecked={isTagChecked}
        />
      );
    case "checkbox":
      return (
        <TagCheckBox
          key={`checkbox-${item.param}`}
          label={item.prompt}
          param={item.param}
          variant={variant}
          prompt={item.mo}
          required={item.required}
          onAlert={onAlert}
          onChange={handleFieldChange}
          value={item.value}
          isAlwaysOn={element?.alwaysOn}
          validationCase={validationCase}
          isTagChecked={isTagChecked}
        />
      );
    case "memo":
      return (
        <TagMemo
          key={`memo-${item.param}`}
          label={item.prompt}
          param={item.param}
          variant={variant}
          prompt={item.mo}
          maxLength={item.length}
          required={item.required}
          onAlert={onAlert}
          onChange={handleFieldChange}
          value={item.value}
          isAlwaysOn={element?.alwaysOn}
          validationCase={validationCase}
          isTagChecked={isTagChecked}
        />
      );
    default:
      return null;
  }
});


const TagContainer = (props) => {
  const tagId = props.info?.quoteTagID;
  const containerRef = useRef(null);

  const [checked, setChecked] = useState(props.info?.checked || false);
  const [fieldValidation, setFieldValidation] = useState({});

  useEffect(() => {
    setChecked(props.info?.checked || false);
  }, [props.info?.checked]);

  // Focus logic... (remains the same)
  useEffect(() => {
    if (checked && props.isFirstTag) {
      setTimeout(() => {
        const firstInput = containerRef.current?.querySelector('input, textarea');
        if (firstInput) {
          console.log('[TagContainer] Focusing on first input of first tag');
          firstInput.focus();
          if (firstInput.value) {
            firstInput.select();
          }
        }
      }, 100);
    }
  }, [checked, props.isFirstTag, props.isTagsAccordionActive]);


  // handleCheck logic... (remains the same)
   const handleCheck = useCallback(() => {
    if (props.info?.alwaysOn) {
      return;
    }

    const newChecked = !checked;
    setChecked(newChecked);

    if (props.onCheckedChange) {
      props.onCheckedChange(props.info.quoteTagID, newChecked);
    }

    // No alert handling here - all validation is done at the Next button level
  }, [checked, props.info, props.onCheckedChange]);


  // handleFieldChange logic... (remains the same)
   const handleFieldChange = useCallback((fieldData) => {
    if (fieldData.isValid !== undefined) {
      setFieldValidation(prev => ({
        ...prev,
        [fieldData.name]: fieldData.isValid
      }));
    }

    if (props.onChange) {
      props.onChange({
        name: fieldData.name,
        value: fieldData.value
      });
    }

    // No alert handling here - all validation is done at the Next button level
  }, [props.onChange]);


  // getTabIndex logic... (remains the same)
  const getTabIndex = useCallback((index) => {
    return 100 + index;
  }, []);

  // getGridSizing logic... (remains the same)
  const getGridSizing = useCallback((totalFields) => {
    const fieldCount = totalFields;

    if (fieldCount === 1) {
      return { xs: 12, sm: 12, md: 12, lg: 12 };
    } else if (fieldCount === 2) {
      return { xs: 12, sm: 12, md: 6, lg: 6 };
    } else if (fieldCount === 3) {
      return { xs: 12, sm: 12, md: 6, lg: 4 };
    } else {
      return { xs: 12, sm: 12, md: 6, lg: 6 };
    }
  }, []);

  const nonNewlineFields = props.restrictions?.filter(item => !item.newline)?.length || 0;

  const renderTagField = useCallback((item, index) => {
    const tabIdx = getTabIndex(index);
    const gridSizing = getGridSizing(nonNewlineFields);

    // Skip newline separators when all fields fit on one row (3 or fewer)
    if (item.newline) {
      return nonNewlineFields <= 3 ? null : <Box key={`newline-${index}`} width="100%" />;
    }

    return (
        <Grid
          item
          {...gridSizing}
          key={`field-${index}`}
          style={{
            marginBottom: '8px',
            paddingLeft: '16px',
          }}
        >
          <TagField
            item={item}
            onChange={handleFieldChange}
            onAlert={props.onAlert}
            variant={props.variant || "outlined"} // Pass down a default variant
            element={props.element || props.info}
            isTagChecked={checked}
          />
        </Grid>
    );
  }, [getGridSizing, getTabIndex, handleFieldChange, nonNewlineFields, props.element, props.info, props.onAlert, props.variant, checked]);

  // Color determination logic... (remains the same)
  // Determine colors
  const dbTagColor = props.info?.tagColor;
  const dbTagTextColor = props.info?.tagTextColor;

  const defaultInactiveBorderColor = '#9e9e9e';
  const defaultCheckedBorderColorFromCSS = '#337AB7';

  const currentBorderColor = dbTagColor || (checked ? defaultCheckedBorderColorFromCSS : defaultInactiveBorderColor);
  const currentBackgroundColor = dbTagColor
    ? dbTagColor
    : (checked ? 'white' : '#f0f0f0');
  const currentTextColor = dbTagTextColor || '#000000';


  return (
    <Grid
      ref={containerRef}
      container
      spacing={2}
    >
      {/* Tag Button Grid Item */}
      <Grid item xs={6} md={2} lg={2}>
         {/* ThemeProvider and Button rendering ... (remains the same) */}
          <ThemeProvider theme={theme}>
          <StyledToggleButtonGroup
            size="small"
            value={checked ? 'selected' : 'not-selected'}
            exclusive
            onChange={handleCheck}
            disabled={props.info?.alwaysOn}
            tabIndex="-1"
            style={{ width: '100%' }}
          >
            <StyledToggleButton
              value="selected"
              disableRipple
              className={`tag-button ${props.info?.alwaysOn ? 'always-on' : ''}`}
              style={{
                cursor: props.info?.alwaysOn ? 'not-allowed' : 'pointer',
                width: '100%',
              }}
              tabIndex="-1"
            >
              <Box
                className={`label-wrapper-no-color ${checked ? 'tag-checked' : ''}`}
                style={{
                  ...styles.labelWrapperNoColor,
                  '--tag-border-color': currentBorderColor,
                  '--tag-bg-color': currentBackgroundColor,
                  '--tag-text-color': currentTextColor,
                  opacity: checked ? 1 : 0.7,
                  width: '100%',
                  minHeight: '40px', // Ensures button label height consistency
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {props.info?.icon_url && <img
                  src={props.info.icon_url}
                  alt={props.label}
                  className="tag-icon"
                  style={{ marginRight: '8px', minWidth: '40px', minHeight: '40px' }}
                />}
                <Typography variant='subtitle1'
                  className="label-no-color"
                  style={{
                    ...styles.labelNoColor,
                    color: currentTextColor,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word'
                  }}
                >
                  {props.info?.label || props.label}
                  {props.info?.alwaysOn ? " (Required)" : ""}
                </Typography>
              </Box>
            </StyledToggleButton>
          </StyledToggleButtonGroup>
        </ThemeProvider>
      </Grid>

      {/* Input Fields Grid Item */}
      <Grid
        item
        xs={12}
        sm={8}
        md={7}
        lg={5}
        className="reference" // This class might affect layout
      >
        {checked && props.restrictions && (
          <Grid
            container
            spacing={0} // Use 0 spacing if inputs are tightly packed
            style={{ width: '100%' }}
            alignItems="flex-start" // Align items to the top
          >
            {props.restrictions.map((item, index) => renderTagField(item, index))}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default memo(TagContainer);